import * as React from 'react'

import {
  Alert,
  Box,
  RadioButton,
  Text,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import NumberField from 'modules/core/components/NumberField'
import i18n from 'modules/core/lib/i18n'

import {
  BankHolidaysTreatmentOptions,
  WizardWorkingConditions,
} from 'modules/job_catalog/components/WorkingConditionsModal/models/wizardWorkingConditions'
import { MAX_VALUES, validateField } from 'modules/job_catalog/lib/workingConditions'

const ConditionsStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<WizardWorkingConditions>) => {
  const [errors, setErrors] = React.useState<
    Partial<Record<keyof WizardWorkingConditions, string>>
  >({})

  React.useEffect(() => {
    if (!data.bankHolidaysTreatment) {
      onDataChanged({ ...data, bankHolidaysTreatment: BankHolidaysTreatmentOptions.NON_WORKING })
    }
  }, [data, onDataChanged])

  React.useEffect(() => {
    const validationErrors: Partial<Record<keyof WizardWorkingConditions, string>> = {}

    if (data.maxAnnualHours !== null && data.maxAnnualHours !== undefined) {
      const error = validateField('ANNUAL_HOURS', data.maxAnnualHours)
      if (error) validationErrors.maxAnnualHours = error
    }

    if (data.maxWeeklyHours !== null && data.maxWeeklyHours !== undefined) {
      const error = validateField('WEEKLY_HOURS', data.maxWeeklyHours)
      if (error) validationErrors.maxWeeklyHours = error
    }

    if (data.maxDailyHours !== null && data.maxDailyHours !== undefined) {
      const error = validateField('DAILY_HOURS', data.maxDailyHours)
      if (error) validationErrors.maxDailyHours = error
    }

    if (data.minRestHoursBetweenDays !== null && data.minRestHoursBetweenDays !== undefined) {
      const error = validateField('REST_HOURS_BETWEEN_DAYS', data.minRestHoursBetweenDays)
      if (error) validationErrors.minRestHoursBetweenDays = error
    }

    onDataChanged({ ...data, stepWithError: Object.keys(validationErrors).length > 0 })
    setErrors(validationErrors)
  }, [data.maxAnnualHours, data.maxWeeklyHours, data.maxDailyHours, data.minRestHoursBetweenDays])

  return (
    <Box flexDirection="column">
      <Text.Header2>
        {i18n.t('job_catalog.nodes.working_conditions.modal.conditions.title')}
      </Text.Header2>
      <Box flexDirection="row" paddingTop="s24">
        <Box width="s400" gap="s24" paddingRight="s24">
          <NumberField
            label={i18n.t('job_catalog.nodes.working_conditions.conditions.max_annual_hours')}
            value={data.maxAnnualHours}
            onChange={(value) => {
              const error = validateField('ANNUAL_HOURS', value)
              setErrors({ ...errors, maxAnnualHours: error })
              onDataChanged({ ...data, maxAnnualHours: value })
            }}
            step={1}
            error={errors.maxAnnualHours}
            min={0}
            max={MAX_VALUES.ANNUAL_HOURS}
          />

          <NumberField
            label={i18n.t('job_catalog.nodes.working_conditions.conditions.max_weekly_hours')}
            value={data.maxWeeklyHours}
            onChange={(value) => {
              const error = validateField('WEEKLY_HOURS', value)
              setErrors({ ...errors, maxWeeklyHours: error })
              onDataChanged({ ...data, maxWeeklyHours: value })
            }}
            step={1}
            error={errors.maxWeeklyHours}
            min={0}
            max={MAX_VALUES.WEEKLY_HOURS}
          />

          <NumberField
            label={i18n.t('job_catalog.nodes.working_conditions.conditions.max_daily_hours')}
            value={data.maxDailyHours}
            onChange={(value) => {
              const error = validateField('DAILY_HOURS', value)
              setErrors({ ...errors, maxDailyHours: error })
              onDataChanged({ ...data, maxDailyHours: value })
            }}
            step={1}
            error={errors.maxDailyHours}
            min={0}
            max={MAX_VALUES.DAILY_HOURS}
          />

          <NumberField
            label={i18n.t(
              'job_catalog.nodes.working_conditions.conditions.min_rest_hours_between_days'
            )}
            value={data.minRestHoursBetweenDays}
            onChange={(value) => {
              const error = validateField('REST_HOURS_BETWEEN_DAYS', value)
              setErrors({ ...errors, minRestHoursBetweenDays: error })
              onDataChanged({
                ...data,
                minRestHoursBetweenDays: value,
              })
            }}
            step={1}
            error={errors.minRestHoursBetweenDays}
            min={0}
            max={MAX_VALUES.REST_HOURS_BETWEEN_DAYS}
          />

          <Box gap="s16">
            <Box gap="s8">
              <Text.Header3>
                {i18n.t(
                  'job_catalog.nodes.working_conditions.conditions.bank_holidays_treatment.title'
                )}
              </Text.Header3>
            </Box>
            <Box gap="s16">
              <RadioButton
                label={i18n.t(
                  'job_catalog.nodes.working_conditions.conditions.bank_holidays_treatment.non_working'
                )}
                value={BankHolidaysTreatmentOptions.NON_WORKING}
                onChange={({ target: { value } }) =>
                  onDataChanged({ ...data, bankHolidaysTreatment: value })
                }
                checked={data.bankHolidaysTreatment === BankHolidaysTreatmentOptions.NON_WORKING}
              />
              <RadioButton
                label={i18n.t(
                  'job_catalog.nodes.working_conditions.conditions.bank_holidays_treatment.working'
                )}
                value={BankHolidaysTreatmentOptions.WORKING}
                onChange={({ target: { value } }) =>
                  onDataChanged({ ...data, bankHolidaysTreatment: value })
                }
                checked={data.bankHolidaysTreatment === BankHolidaysTreatmentOptions.WORKING}
              />
            </Box>
          </Box>
        </Box>
        <Box maxWidth="s300">
          <Box height="s140">
            <Alert color="info">
              {i18n.t('job_catalog.nodes.working_conditions.modal.conditions.one_field_mandatory')}
            </Alert>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

const isCompleted = (data: WizardWorkingConditions): boolean => {
  if (data.stepWithError) {
    return false
  }

  return (
    !!data.maxAnnualHours ||
    !!data.minRestHoursBetweenDays ||
    !!data.maxDailyHours ||
    !!data.maxWeeklyHours
  )
}

export const conditionsStep = (
  isNewWorkingCondition: boolean
): WizardStep<WizardWorkingConditions> => ({
  title: i18n.t('job_catalog.nodes.working_conditions.modal.conditions.title'),
  nextText: isNewWorkingCondition
    ? i18n.t('job_catalog.nodes.working_conditions.modal.submit')
    : i18n.t('job_catalog.nodes.working_conditions.modal.submitEdit'),
  render: (props) => <ConditionsStep {...props} />,
  isCompleted,
})
